import { ClientUser, UserRole } from '@numeo/types-core'
import { TeamDriverRow } from '@numeo/types-main-web-app'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { useMemo } from 'react'
import { useAppSelector } from '@/hooks'
import { selectTeamApplicationId, useTeamDirectoryEnabled } from '@/hooks/team/useTeamDirectoryEnabled'
import { useGetScopedDriverRosterQuery, useListTeamDriversQuery } from '@/store/api/teamApi'
import { listDriversFromUsers } from '@/utils/team/teamQuery'

interface AllTeamDriversState {
    /** Every driver the caller may see, in name order. */
    rows: TeamDriverRow[]
    /** Some page has not answered yet. */
    isLoading: boolean
    /** A page could not be read: the list is not to be offered as complete. */
    isError: boolean
}

interface AllTeamDriversOptions {
    /** Before the directory serves the company, the Groups-scoped roster rather than `application.users`. */
    scoped?: boolean
    skip?: boolean
}

const NO_ROWS: TeamDriverRow[] = []
const NO_USERS: ClientUser[] = []

/**
 * Every driver of the company the caller may see, for the pickers that search their own option list (ds2
 * `Autocomplete` searches the options it holds): ONE `GET /team/drivers/v1?all=true`, cached five minutes like any list.
 * Screens that page and search on the server use `useTeamDrivers` instead.
 */
export const useAllTeamDrivers = (options: AllTeamDriversOptions = {}): AllTeamDriversState => {
    const enabled = useTeamDirectoryEnabled()
    const applicationId = useAppSelector(selectTeamApplicationId)
    const skip = !!options.skip
    const scopedRoster = !enabled && !!options.scoped && !skip

    const roster = useGetScopedDriverRosterQuery(scopedRoster && applicationId ? { applicationId } : skipToken)
    const users = useAppSelector((state) => (enabled || scopedRoster ? undefined : state.auth.application?.users))
    const everyone = useListTeamDriversQuery(enabled && applicationId && !skip ? { applicationId, all: true } : skipToken)

    return useMemo((): AllTeamDriversState => {
        if (skip) return { rows: NO_ROWS, isLoading: false, isError: false }
        if (!enabled) {
            const source = scopedRoster ? (roster.data ?? NO_USERS) : (users ?? NO_USERS)
            const rows = source.filter((user) => user.role === UserRole.DRIVER)
            return {
                rows: rows.length ? listDriversFromUsers(rows, { page: 1, limit: rows.length }).items : NO_ROWS,
                isLoading: scopedRoster && roster.isLoading,
                isError: scopedRoster && roster.isError,
            }
        }
        return { rows: everyone.data?.items ?? NO_ROWS, isLoading: !everyone.data && !everyone.isError, isError: everyone.isError }
    }, [skip, enabled, scopedRoster, roster.data, roster.isLoading, roster.isError, users, everyone.data, everyone.isError])
}
